import { supabase } from './supabase';
import { callRpcViaFetch } from './rpc-bypass';
import * as Sentry from '@sentry/react';
import { Filters } from '../types';

export type UserRole = 'user' | 'admin';

export interface Profile {
  id: string;
  name: string | null;
  avatar_url: string | null;
  role: UserRole;
  whatsapp_number: string | null;
  bio: string | null;
  website_url: string | null;
  agency_name: string | null;
  license_number: string | null;
  user_type: 'broker' | 'seller' | null;
}

/**
 * Get a single user's profile
 * Uses raw fetch bypass for fast, deadlock-free data loading
 */
export async function getProfile(userId: string): Promise<Profile | null> {
  try {
    const { data, error } = await callRpcViaFetch('get_profile', { user_id: userId });

    if (error) {
      console.error('[getProfile] RPC error:', error);
      throw error;
    }
    
    // RPC returns an array, get first item
    return (data as any[])?.[0] || null;
  } catch (error) {
    console.error('[getProfile] ERROR:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'profiles', action: 'get-profile' },
      extra: { userId }
    });
    return null;
  }
}

/**
 * Get multiple user profiles (for Stories Bar)
 */
export async function getProfiles(userIds: string[]): Promise<Profile[]> {
  if (userIds.length === 0) return [];
  
  try {
    const { data, error } = await supabase
      .rpc('get_profiles', { user_ids: userIds });

    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('Error fetching profiles:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'profiles', action: 'get-profiles' } 
    });
    return [];
  }
}

/**
 * Update the current user's profile
 */
export async function updateProfile(data: { 
  name?: string; 
  avatar_url?: string;
  whatsapp_number?: string;
  bio?: string;
  website_url?: string;
  agency_name?: string;
  license_number?: string;
  user_type?: 'broker' | 'seller';
}): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { error } = await supabase
      .from('profiles')
      .upsert({
        id: user.id,
        ...data,
        updated_at: new Date().toISOString()
      });

    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('Error updating profile:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'profiles', action: 'update-profile' } 
    });
    return false;
  }
}

/**
 * Ensure current user has a profile (call after signup/login)
 */
export async function ensureProfile(): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Check if profile exists
    const { data: existing } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', user.id)
      .single();

    if (!existing) {
      // Create profile from user metadata
      await supabase
        .from('profiles')
        .insert({
          id: user.id,
          name: user.user_metadata?.name || user.user_metadata?.full_name || user.email?.split('@')[0],
          avatar_url: user.user_metadata?.avatar_url
        });
    }
  } catch (error) {
    // Ignore errors - profile might already exist or trigger handles it
    if (import.meta.env.DEV) {
      console.log('Profile ensure check:', error);
    }
  }
}

export interface UserFilters {
  listingType: string | null;
  city: string | null;
  minPrice: number | null;
  maxPrice: number | null;
  minRooms: number | null;
  maxRooms: number | null;
  minSqm: number | null;
  maxSqm: number | null;
  moveInUrgency: string | null;
  amenities: string[] | null;
}

/**
 * Get user filters with broker-specific priority, fallback to global
 * Uses the universal subscriptions model (broker_id = NULL for global, UUID for broker-specific)
 */
export async function getUserFilters(brokerId?: string): Promise<UserFilters | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase.rpc('get_user_filters', {
      p_user_id: user.id,
      p_broker_id: brokerId || null,
    });

    if (error) {
      // If no filters found, return null (not an error)
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }

    // RPC returns array, get first result
    const result = Array.isArray(data) ? data[0] : data;
    
    if (!result) return null;

    return {
      listingType: result.listing_type,
      city: result.city,
      minPrice: result.min_price,
      maxPrice: result.max_price,
      minRooms: result.min_rooms,
      maxRooms: result.max_rooms,
      minSqm: result.min_sqm,
      maxSqm: result.max_sqm,
      moveInUrgency: result.move_in_urgency,
      amenities: result.amenities || [],
    };
  } catch (error) {
    console.error('Error fetching user filters:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'profiles', action: 'get-user-filters' } 
    });
    return null;
  }
}

/**
 * Save the current user's global search preferences
 * Saves to listing_subscriptions with broker_id = NULL
 * Called automatically when filters change on the Discover/Preferences page
 */
export async function saveSearchPreferences(filters: Filters): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Extract city name from location if available
    const cityName = filters.location?.city || filters.location?.name || null;
    
    // Combine amenities with petFriendly if set
    const amenities = [...filters.amenities];
    if (filters.petFriendly === true && !amenities.includes('petFriendly')) {
      amenities.push('petFriendly');
    }

    const { error } = await supabase.rpc('save_search_preferences', {
      p_listing_type: filters.listingType,
      p_city: cityName,
      p_min_price: filters.minPrice,
      p_max_price: filters.maxPrice,
      p_rooms_min: filters.minRooms,
      p_rooms_max: filters.maxRooms,
      p_min_sqm: filters.minSqm,
      p_max_sqm: filters.maxSqm,
      p_move_in_urgency: filters.moveInUrgency,
      p_amenities: amenities,
    });

    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('Error saving search preferences:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'profiles', action: 'save-preferences' } 
    });
    return false;
  }
}

/**
 * Update all active listing subscriptions for the current user with new filter preferences
 * This updates WhatsApp alert subscriptions to match the user's saved preferences
 */
export async function updateListingSubscriptions(filters: Filters): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Fetch all active subscriptions for this user
    const { data: subscriptions, error: fetchError } = await supabase
      .from('listing_subscriptions')
      .select('id')
      .eq('subscriber_user_id', user.id)
      .eq('is_active', true);

    if (fetchError) throw fetchError;

    if (!subscriptions || subscriptions.length === 0) {
      // No subscriptions to update - this is fine
      return true;
    }

    // Extract city name from location if available
    const cityName = filters.location?.city || filters.location?.name || null;

    // Prepare metadata JSONB
    const metadata: any = {
      amenities: filters.amenities,
      petFriendly: filters.petFriendly === true,
      radius: filters.radius,
    };

    // Add location to metadata if available
    if (filters.location) {
      metadata.location = {
        lat: filters.location.lat,
        lng: filters.location.lng,
        name: filters.location.name,
        streetAddress: filters.location.streetAddress || null,
        city: filters.location.city || null,
      };
    } else {
      metadata.location = null;
    }

    // Update all subscriptions in batch
    const { error: updateError } = await supabase
      .from('listing_subscriptions')
      .update({
        listing_type: filters.listingType,
        city: cityName,
        min_price: filters.minPrice,
        max_price: filters.maxPrice,
        min_rooms: filters.minRooms,
        max_rooms: filters.maxRooms,
        min_sqm: filters.minSqm,
        max_sqm: filters.maxSqm,
        move_in_urgency: filters.moveInUrgency,
        metadata: metadata,
      })
      .eq('subscriber_user_id', user.id)
      .eq('is_active', true);

    if (updateError) throw updateError;

    return true;
  } catch (error) {
    console.error('Error updating listing subscriptions:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'profiles', action: 'update-subscriptions' } 
    });
    return false;
  }
}
